/* eslint-disable no-shadow */
import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Cookies from 'universal-cookie';
import { applyContainerQuery } from 'react-container-query';
import {
  AuthContainer,
  containerQueryConstants,
  containerQueryProps,
  utils,
  authSteps,
} from '@resy/ui-kitchen';
import { IDLE, asyncLoadingStatusTypes } from '../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import { loadResyConfigThunk } from '../../../../../ui-kitchen/src/reducers';
import { selectIsNonUS } from '../../../../../ui-kitchen/src/reducers/userSlice';

const getPath = (path) => {
  const key = {
    '/account/payment-methods/add-card': '/account/payment-methods',
  };

  return key[path] || path;
};

const WrappedAuthContainer = ({
  $location,
  $rootScope,
  $route,
  analyticsProps,
  AutomationMode,
  callingCode,
  containerQuery,
  Analytics: { event },
  close,
  firstStep,
  isNonUS,
  successCallback,
  countryCodes,
  configStatus,
  deviceId,
  loadResyConfigThunk,
  ResyAmexConfig,
  setContentLabel,
}) => {
  const cookies = new Cookies();
  const [deviceIdState, setDeviceIdState] = useState(null);
  const [next, setNext] = useState(undefined);
  const [cookieDeltaPage] = useState(cookies.get('_deltaPage_'));

  const path = (url) => {
    close();

    if (!url) { return; }

    let paramsToString = '';
    const entries = Object.entries($location.search() || {});
    if (entries.length !== 0) {
      paramsToString = entries.reduce(
        (iter, val) => `${iter}${val.join('=')}&`,
        '?'
      );
    }

    $location.url(`${url}${paramsToString}`);
    $route.reload();
  };

  useEffect(() => {
    let nextPath = null;

    if ($rootScope?.nav?.backPath) {
      nextPath = getPath($rootScope?.nav?.backPath);
    }

    setNext(nextPath);

    if ($rootScope?.nav) {
      $rootScope.nav.backPath = null;
    }

    const [uuid, removeListener] = utils.getUuid(deviceId);

    setDeviceIdState(uuid);

    if (configStatus === IDLE) {
      loadResyConfigThunk();
    }

    return () => {
      removeListener();
    };
  }, [deviceId, configStatus, $rootScope]);

  let firstStepValue = firstStep;
  if (cookieDeltaPage === 'Y' && firstStep !== authSteps.SIGNUP) {
    firstStepValue = authSteps.LOGIN_EMAIL_PASSWORD;
  }

  return (
    <AuthContainer
      analyticsProps={analyticsProps}
      AutomationMode={AutomationMode}
      close={close}
      containerQuery={containerQuery}
      countryCodes={countryCodes}
      defaultCountryCode={callingCode}
      deviceId={deviceIdState}
      event={event}
      grecaptcha={window.grecaptcha}
      isNonUS={isNonUS}
      marketingOptInChecked={false}
      next={next}
      path={path}
      successCallback={successCallback}
      firstStep={firstStepValue}
      location={$location}
      ResyAmexConfig={ResyAmexConfig}
      setContentLabel={setContentLabel}
    />
  );
};

WrappedAuthContainer.propTypes = {
  $location: PropTypes.shape({
    search: PropTypes.func,
    url: PropTypes.func.isRequired,
  }).isRequired,
  $rootScope: PropTypes.shape({
    nav: PropTypes.shape(),
  }).isRequired,
  $route: PropTypes.shape({
    reload: PropTypes.func,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  analyticsProps: PropTypes.object,
  AutomationMode: PropTypes.bool.isRequired,
  callingCode: PropTypes.string,
  containerQuery: containerQueryProps,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  close: PropTypes.func.isRequired,
  firstStep: PropTypes.string,
  isNonUS: PropTypes.bool.isRequired,
  successCallback: PropTypes.func,
  countryCodes: PropTypes.arrayOf(PropTypes.shape({})),
  configStatus: asyncLoadingStatusTypes.isRequired,
  deviceId: PropTypes.string.isRequired,
  loadResyConfigThunk: PropTypes.func.isRequired,
  ResyAmexConfig: PropTypes.shape({
    baseRequestUrl: PropTypes.string.isRequired,
  }).isRequired,
  setContentLabel: PropTypes.func,
};

WrappedAuthContainer.defaultProps = {
  analyticsProps: {},
  callingCode: '1',
  containerQuery: {},
  countryCodes: [{ iso3166: 'US', code: '1', country_name: 'United States' }],
  firstStep: undefined,
  successCallback: () => {},
  setContentLabel: () => {},
};

const mapStateToProps = (state) => {
  const { resyConfig } = state;
  return {
    configStatus: resyConfig.configStatus,
    countryCodes: resyConfig.config?.country_codes,
    isNonUS: selectIsNonUS(state),
    deviceId: resyConfig.deviceId,
  };
};

const mapDispatchToProps = {
  loadResyConfigThunk,
};

// Naming for AuthContainer-releated items is a mess, and not trying to sort it out here
const ContainerQueryWrappedAuthContainer = applyContainerQuery(WrappedAuthContainer, containerQueryConstants);
export { WrappedAuthContainer as WrappedComponent, getPath };
export default connect(mapStateToProps, mapDispatchToProps)(ContainerQueryWrappedAuthContainer);
